import { useEffect, useMemo, useRef } from "react";

import { getText, interpolate } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type ReasonTreePropertyDescriptor,
  type RemoveOrPruneTreePropertyDescriptor,
  type SizeEstimateTreePropertyDescriptor,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Textarea, Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface RemoveOrPruneReasonProps {
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function RemoveOrPruneReasonField<
  T extends ReasonTreePropertyDescriptor,
>({ onChange, treeDescriptor }: RemoveOrPruneReasonProps) {
  const defaultValue = useMemo(() => getText("It's in the way"), []);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.reason,
    },
  );

  const trunkType = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as TrunkTypeTreePropertyDescriptor | undefined;

  const sizeEstimate = treeDescriptor.properties.get(TreePropertyType.size) as
    | SizeEstimateTreePropertyDescriptor
    | undefined;

  const removeOrPrune = treeDescriptor.properties.get(
    TreePropertyType.remove_or_prune,
  ) as RemoveOrPruneTreePropertyDescriptor | undefined;

  const reason = treeDescriptor.properties.get(TreePropertyType.reason) as
    | T
    | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.reason,
      (draft) => {
        draft.value = value;
      },
    );

    onChange?.(newTreeDescriptor);
  };

  // Scroll to textarea when it becomes visible
  useEffect(() => {
    if (reason?.value !== defaultValue && textareaRef.current) {
      textareaRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [reason?.value, defaultValue]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <FieldLabel isValid={isValid}>
          {interpolate(
            "Why are you proposing to {{activity}} this {{thing}}?",
            {
              activity:
                removeOrPrune?.value === "remove"
                  ? getText("remove")
                  : getText("prune"),
              thing:
                trunkType?.value === "vegetation" ||
                sizeEstimate?.value === "vegetation"
                  ? getText("vegetation")
                  : getText("tree"),
            },
          )}
        </FieldLabel>

        <div className="flex gap-2">
          <Toggle
            aria-invalid={!isValid && shouldShowValidation}
            className="aria-invalid:border-redBorder"
            onClick={() => {
              handleChange(defaultValue);
            }}
            pressed={reason?.value === defaultValue}
            variant="rounded"
          >
            {getText(defaultValue)}
          </Toggle>

          <Toggle
            aria-invalid={!isValid && shouldShowValidation}
            className="aria-invalid:border-redBorder"
            onClick={() => {
              handleChange("");
            }}
            pressed={reason?.value !== defaultValue}
            variant="rounded"
          >
            {getText("Other reason")}
          </Toggle>
        </div>
      </div>

      {reason?.value !== defaultValue && (
        <div className="flex flex-col gap-1">
          <FieldLabel isValid={isValid}>
            {getText("Enter the reason")}
          </FieldLabel>

          <Textarea
            autoFocus
            className="invalid-sibling-label:border-redDefault"
            onChange={(event) => {
              handleChange(event.target.value);
            }}
            placeholder={getText(
              "E.g. it's dangerous in some way, it's dying, bushfire prevention",
            )}
            ref={textareaRef}
            value={reason?.value || ""}
          />
        </div>
      )}

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
